import type {
  ChartCard,
  ChartSeries,
  Timeframe,
} from '@/features/dashboard/api/schemas'
import { chartTotals } from '@/features/dashboard/format/format'
import type { ChartPalette } from './chartTheme'

/** Labels for the benchmark series. */
const REFERENCE_LABELS: Record<string, string> = {
  metric_revised_budget_upside: 'Upside forecast',
  metric_revised_budget_base: 'Base forecast',
  metric_budget_upside: 'Upside budget',
  metric_budget_base: 'Base Budget',
  metric_monthly_forecast: 'Forecast',
  metric_outlook: 'Outlook',
  target: 'Target',
}

/** The projection for the months still to come. */
export const OUTLOOK_SERIES = 'metric_outlook'

/** Series names are matched case-insensitively, as the backend's casing varies. */
const sameName = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

/** The first series called `name`, whatever its case. */
export const findSeries = <T extends Pick<ChartSeries, 'name'>>(
  series: T[],
  name: string
): T | undefined => series.find(entry => sameName(entry.name, name))

/** Group splits join the group and the series: 'DEU__PAU' is shown as 'DEU — PAU'. */
const GROUP_SEPARATOR = /__| — /

export const isReference = (series: Pick<ChartSeries, 'reference'>) =>
  series.reference === true

/** The series that are the metric itself, not benchmarks. */
export const metricSeries = <T extends Pick<ChartSeries, 'reference'>>(
  series: T[]
): T[] => series.filter(entry => !isReference(entry))

/** The benchmarks (target, budget, forecast, outlook), drawn next to the metric. */
export const referenceSeries = <T extends Pick<ChartSeries, 'reference'>>(
  series: T[]
): T[] => series.filter(isReference)

/** A series as the legend and tooltip name it. */
export const seriesLabel = (name: string): string =>
  REFERENCE_LABELS[name] ?? name.split(GROUP_SEPARATOR).join(' — ')

/**
 * The colour of a named benchmark's line, as the design draws them: the revised
 * budget dark (base) and pink (upside), the original budget teal (base) and
 * blue (upside). Others take their turn in the palette.
 */
export const referenceColorFor = (
  name: string,
  palette: ChartPalette,
  index: number
): string => {
  switch (name.toLowerCase()) {
    case 'metric_revised_budget_base':
      return palette.text
    case 'metric_revised_budget_upside':
      return palette.series[1] ?? palette.goal
    case 'metric_budget_base':
      return palette.series[2] ?? palette.goal
    case 'metric_budget_upside':
      return palette.series[0] ?? palette.goal
    case 'target':
      return palette.goal
    default:
      return referenceColor(palette, index)
  }
}

/** The colour of the nth benchmark line: the target's, then the series'. */
export const referenceColor = (palette: ChartPalette, index: number): string =>
  index === 0
    ? palette.goal
    : (palette.series[(index - 1) % Math.max(palette.series.length, 1)] ??
      palette.goal)

/**
 * The colour of a change as the badge shows it: green when it is good news
 * (a rise, or a fall where lower is better), red when bad; none for no change.
 */
export const changeColor = (
  change: number | null | undefined,
  palette: ChartPalette,
  lowerIsBetter: boolean
): string | undefined =>
  change == null || change === 0
    ? undefined
    : change > 0 !== lowerIsBetter
      ? palette.positive
      : palette.negative

/**
 * A lone series is coloured by its last movement, as the home tiles' trend
 * lines are: green if the last point rose, red if it fell (the other way round
 * where lower is better).
 */
export const trendColor = (
  data: (number | null)[],
  palette: ChartPalette,
  lowerIsBetter: boolean
): string => {
  const values = data.filter((value): value is number => value !== null)
  const last = values.at(-1) ?? 0
  const previous = values.at(-2) ?? last
  return last >= previous !== lowerIsBetter
    ? palette.positive
    : palette.negative
}

/**
 * A chart's per-category headline series for a tile in the YTD view: the metric
 * itself, carrying on with the `metric_outlook` reference where the year has no
 * value yet. Without an outlook it is `totals`.
 */
export const fillWithOutlook = (
  card: Pick<ChartCard, 'totals' | 'series'>
): (number | null)[] => {
  const totals = chartTotals(card)
  const outlook = findSeries(referenceSeries(card.series), OUTLOOK_SERIES)
  if (!outlook) return totals
  const metrics = metricSeries(card.series)
  return totals.map((value, i) => {
    const reported =
      metrics.length > 0
        ? metrics.some(entry => typeof entry.data[i] === 'number')
        : value !== null
    return reported ? value : (outlook.data[i] ?? null)
  })
}

/**
 * The index of the latest category with a reported value: a YTD year runs on to December, its trailing months
 * still empty. -1 when nothing is reported.
 */
export const latestDataIndex = (
  card: Pick<ChartCard, 'totals' | 'series' | 'categories'>
): number => {
  const metrics = metricSeries(card.series)
  for (let i = card.categories.length - 1; i >= 0; i--) {
    const reported =
      metrics.length > 0
        ? metrics.some(entry => typeof entry.data[i] === 'number')
        : typeof card.totals[i] === 'number'
    if (reported) return i
  }
  return -1
}

const ORIGINAL_BUDGETS = ['metric_budget_base', 'metric_budget_upside']
const REVISED_BUDGETS = [
  'metric_revised_budget_base',
  'metric_revised_budget_upside',
]
/** The original budget runs to March; the revised one takes over from April. */
const LAST_ORIGINAL_BUDGET_MONTH = 3

/**
 * The (first) month, 1-12, of a category: '2026-08' and '2026-08-31…' are
 * August, a quarter '2026-Q3' is its first month (July). Null for a whole year
 * or anything else, which has no month to decide by.
 */
const periodMonth = (category: string): number | null => {
  const month = /^\d{4}-(\d{2})/.exec(category)
  if (month) return Number(month[1])
  const quarter = /^\d{4}-Q([1-4])$/.exec(category)
  return quarter ? (Number(quarter[1]) - 1) * 3 + 1 : null
}

/**
 * Whether a benchmark applies to a period. Which budget a period is measured
 * against changes during the year: the original (base and upside) up to
 * March, the revised from April. While the current month is itself April or
 * earlier, the original budget is shown as far as it is defined.
 */
export const isShownForPeriod = (
  name: string,
  category: string,
  now: Date = new Date()
): boolean => {
  const month = periodMonth(category)
  if (month === null) return true
  const key = name.toLowerCase()
  if (ORIGINAL_BUDGETS.includes(key))
    return (
      month <= LAST_ORIGINAL_BUDGET_MONTH ||
      now.getMonth() + 1 <= LAST_ORIGINAL_BUDGET_MONTH + 1
    )
  if (REVISED_BUDGETS.includes(key)) return month > LAST_ORIGINAL_BUDGET_MONTH
  return true
}

/**
 * The card with each budget emptied for the periods it does not apply to, so
 * the chart's lines and the panel's comparisons agree on which budget is in
 * force when.
 */
export const maskBudgets = <T extends Pick<ChartCard, 'categories' | 'series'>>(
  card: T,
  now: Date = new Date()
): T => ({
  ...card,
  series: card.series.map(series =>
    isReference(series)
      ? {
          ...series,
          data: series.data.map((value, i) =>
            isShownForPeriod(series.name, card.categories[i] ?? '', now)
              ? value
              : null
          ),
        }
      : series
  ),
})

type Sliceable = Pick<ChartCard, 'categories' | 'series' | 'totals'>

const sliceCard = <T extends Sliceable>(
  card: T,
  start: number,
  end: number
): T => ({
  ...card,
  categories: card.categories.slice(start, end),
  totals: card.totals.slice(start, end),
  series: card.series.map(series => ({
    ...series,
    data: series.data.slice(start, end),
  })),
})

/**
 * The card without the trailing categories nothing is reported for: the
 * benchmarks run on past where the metric stops, but a Monthly, Quarterly or
 * Annual chart ends on the latest period. (The YTD view runs to December.)
 */
export const trimUnreported = <T extends Sliceable>(card: T): T => {
  const keep = latestDataIndex(card) + 1
  return keep <= 0 || keep >= card.categories.length
    ? card
    : sliceCard(card, 0, keep)
}

const MONTHS_PER_PERIOD: Partial<Record<Timeframe, number>> = {
  Quarterly: 3,
  Annual: 12,
}

/**
 * What a section's range shows of a card: up to the latest reported period
 * (except in the YTD view, which runs to December), and no more than the
 * range's months back (all of it for 0). Done here, not by the API, which
 * counts the budgets' months to come among the periods.
 */
export const periodWindow = <T extends Sliceable>(
  card: T,
  timeframe: Timeframe | undefined,
  months: number | undefined
): T => {
  if (timeframe === 'YTD') return card
  const reported = trimUnreported(card)
  if (months === undefined || months <= 0) return reported
  const periods = Math.ceil(
    months / (MONTHS_PER_PERIOD[timeframe ?? 'Monthly'] ?? 1)
  )
  const start = reported.categories.length - periods
  return start > 0
    ? sliceCard(reported, start, reported.categories.length)
    : reported
}
